import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Search, Edit, AlertTriangle, RefreshCw, Package, TrendingUp, TrendingDown, Filter } from 'lucide-react';
import BOLayout from '../BOLayout';
import Pagination from '@/components/back-office/Pagination';

const BOStocks = () => {
    const [loading, setLoading] = useState(true);
    const [products, setProducts] = useState([]);
    const [totalProducts, setTotalProducts] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchType, setSearchType] = useState('name');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(50);
    const [alerts, setAlerts] = useState([]);
    const [alertCount, setAlertCount] = useState(0);
    const [selectedRestaurant, setSelectedRestaurant] = useState(null);
    const [restaurants, setRestaurants] = useState([]);
    const [showResetModal, setShowResetModal] = useState(false);
    const [showResetCategoryModal, setShowResetCategoryModal] = useState(false);
    const [initialStock, setInitialStock] = useState(1000);
    const [isResetting, setIsResetting] = useState(false);
    const [weeklySales, setWeeklySales] = useState([]);
    const [weeklyPeriod, setWeeklyPeriod] = useState({ start: '', end: '' });
    const [stockSummary, setStockSummary] = useState({
        totalStock: 0,
        outOfStock: 0,
        lowStock: 0,
        highestStock: { name: '', value: 0 },
        bestSeller: { name: '', sold: 0 }
    });
    const [selectedProducts, setSelectedProducts] = useState({});
    const [productQuantities, setProductQuantities] = useState({});
    const role = JSON.parse(localStorage.getItem('role'));
    const navigate = useNavigate();

    useEffect(() => {
        const fetchRestaurants = async () => {
            try {
                const email = JSON.parse(localStorage.getItem('user'))?.email;
                const response = await axios.get(
                    `${import.meta.env.VITE_API_URL}/api/back-office/restaurants/${email}/list`
                );
                if (response.data && response.data.length > 0) {
                    setRestaurants(response.data);
                    setSelectedRestaurant(response.data[0]);
                    fetchStockData(response.data[0].id);
                } else {
                    setLoading(false);
                }
            } catch (error) {
                console.error("Erreur lors de la récupération des restaurants:", error);
                toast.error("Impossible de récupérer les restaurants");
                setLoading(false);
            }
        };

        fetchRestaurants();
    }, []);

    const calculateStockSummary = (products) => {
        if (!products || products.length === 0) return;

        let totalStock = 0;
        let outOfStock = 0;
        let lowStock = 0;
        let highestStockProduct = { name: '', value: 0 };
        let bestSellerProduct = { name: '', sold: 0 };

        // Créer un tableau de produits pour ce restaurant uniquement
        const restaurantProducts = products.filter(product => 
            product.restaurant_id === selectedRestaurant?.id
        );

        // Utiliser ce tableau filtré pour les calculs
        restaurantProducts.forEach(product => {
            // Total stock - Vérifier que la valeur est un nombre valide
            const stock = parseInt(product.currentStock, 10);
            if (!isNaN(stock) && stock >= 0) {
                totalStock += stock;
            }
            
            // Out of stock count
            if (product.currentStock === 0) outOfStock++;
            
            // Low stock count (≤ 10)
            if (product.currentStock > 0 && product.currentStock <= 10) lowStock++;
            
            // Highest stock product
            if (product.currentStock > highestStockProduct.value) {
                highestStockProduct = { name: product.name, value: product.currentStock };
            }
            
            // Best seller product - vérifier d'abord que soldInOrders est un nombre valide
            const sold = parseInt(product.soldInOrders, 10) || 0;
            if (sold > bestSellerProduct.sold) {
                bestSellerProduct = { 
                    name: product.name, 
                    sold: sold 
                };
            }
        });

        // Log de débogage pour vérifier les valeurs
        console.log(`Restaurant: ${selectedRestaurant?.name}, Produits: ${restaurantProducts.length}, Meilleur vendeur: ${bestSellerProduct.name} (${bestSellerProduct.sold})`);

        setStockSummary({
            totalStock,
            outOfStock,
            lowStock,
            highestStock: highestStockProduct,
            bestSeller: bestSellerProduct
        });
    };

    // Formater les grands nombres pour l'affichage
    const formatNumber = (number) => {
        if (number === undefined || number === null) return '0';
        
        // Si le nombre est trop grand, utiliser la notation abrégée
        if (number >= 1000000) {
            return (number / 1000000).toFixed(1) + 'M';
        } else if (number >= 1000) {
            return (number / 1000).toFixed(1) + 'K';
        }
        
        // Sinon retourner le nombre formaté avec séparateurs de milliers
        return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    };

    const fetchStockData = async (restaurantId) => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${import.meta.env.VITE_API_URL}/api/back-office/stocks/restaurants/${restaurantId}/statistics`,
                {
                    params: {
                        timestamp: new Date().getTime()
                    }
                }
            );
            
            if (response.data) {
                const allProducts = [];
                
                console.log("DONNÉES API COMPLÈTES:", response.data);
                
                response.data.categorizedStats.forEach((category) => {
                    console.log(`Catégorie: ${category.name}, Nombre de produits: ${category.products.length}`);
                    
                    category.products.forEach((product) => {
                        const soldInOrders = parseInt(product.units_sold || product.soldInOrders || 0, 10);
                        
                        allProducts.push({
                            ...product,
                            category_name: category.name,
                            soldInOrders: soldInOrders || 0,
                        });
                    });
                });
                
                const withSales = allProducts.filter(p => p.soldInOrders > 0);
                console.log(`DIAGNOSTIC: ${withSales.length}/${allProducts.length} produits avec ventes > 0`);
                
                if (withSales.length > 0) {
                    console.log("EXEMPLES DE PRODUITS AVEC VENTES:");
                    withSales.slice(0, 5).forEach(p => {
                        console.log(`- ${p.name}: ${p.soldInOrders} units_sold=${p.units_sold}`);
                    });
                }
                
                setProducts(allProducts);
                setTotalProducts(allProducts.length);
                calculateStockSummary(allProducts);
            }
            
            const alertsResponse = await axios.get(
                `${import.meta.env.VITE_API_URL}/api/back-office/stocks/alerts`
            );
            
            if (alertsResponse.data) {
                const restaurantAlerts = alertsResponse.data.restaurantAlerts.find(
                    alert => alert.restaurant_id === restaurantId
                );
                
                if (restaurantAlerts) {
                    setAlerts(restaurantAlerts.products);
                    setAlertCount(restaurantAlerts.products.length);
                } else {
                    setAlerts([]);
                    setAlertCount(0);
                }
            }
            
            fetchWeeklySales(restaurantId);
            
            setLoading(false);
        } catch (error) {
            console.error("Erreur lors de la récupération des données de stock:", error);
            toast.error("Impossible de récupérer les données de stock");
            setLoading(false);
        }
    };

    // Fonction pour récupérer les ventes hebdomadaires (du vendredi 15h au vendredi suivant 15h)
    const fetchWeeklySales = async (restaurantId) => {
        try {
            const response = await axios.get(
                `${import.meta.env.VITE_API_URL}/api/back-office/stocks/restaurants/${restaurantId}/weekly-sales`
            );
            
            if (response.data) {
                console.log("VENTES HEBDOMADAIRES:", response.data);
                
                // Formater les dates pour l'affichage
                const startDate = new Date(response.data.startDate);
                const endDate = new Date(response.data.endDate);
                
                const formatDateOptions = { 
                    weekday: 'long', 
                    day: 'numeric', 
                    month: 'long',
                    hour: '2-digit',
                    minute: '2-digit'
                };
                
                const formattedStart = startDate.toLocaleDateString('fr-FR', formatDateOptions);
                const formattedEnd = endDate.toLocaleDateString('fr-FR', formatDateOptions);
                
                setWeeklyPeriod({
                    start: formattedStart,
                    end: formattedEnd
                });
                
                // Stocker les données de ventes hebdomadaires
                setWeeklySales(response.data.products);
                
                // Mettre à jour les produits avec les données de ventes hebdomadaires
                setProducts(prevProducts => {
                    return prevProducts.map(product => {
                        // Trouver les ventes hebdomadaires correspondantes
                        const weeklySalesData = response.data.products.find(
                            p => p.id === product.id
                        );
                        
                        return {
                            ...product,
                            weeklySold: weeklySalesData ? weeklySalesData.weeklySold : 0
                        };
                    });
                });
            }
        } catch (error) {
            console.error("Erreur lors de la récupération des ventes hebdomadaires:", error);
            // Ne pas afficher de toast pour ne pas perturber l'expérience utilisateur
        }
    };

    const handleRestaurantChange = (e) => {
        const restaurantId = e.target.value;
        const restaurant = restaurants.find(r => r.id === restaurantId);
        if (restaurant) {
            setSelectedRestaurant(restaurant);
            fetchStockData(restaurantId);
        }
    };

    const handleProductSelect = (productId) => {
        setSelectedProducts(prev => ({
            ...prev,
            [productId]: !prev[productId]
        }));

        // Initialiser les quantités si le produit est sélectionné
        if (!selectedProducts[productId]) {
            setProductQuantities(prev => ({
                ...prev,
                [productId]: {
                    initial: 0,
                    weekly: 0
                }
            }));
        }
    };

    const handleQuantityChange = (productId, type, value) => {
        const quantity = Math.max(0, parseInt(value, 10) || 0);
        
        setProductQuantities(prev => ({
            ...prev,
            [productId]: {
                ...prev[productId],
                [type]: quantity
            }
        }));
    };

    const handleResetStocks = async () => {
        if (!selectedRestaurant) return;
        
        // Vérifier qu'au moins un produit est sélectionné
        const selectedCount = Object.values(selectedProducts).filter(Boolean).length;
        if (selectedCount === 0) {
            toast.error("Veuillez sélectionner au moins un produit");
            return;
        }

        // Vérifier que tous les produits sélectionnés ont un stock initial valide
        const invalidProducts = Object.entries(selectedProducts)
            .filter(([_, selected]) => selected)
            .filter(([id]) => {
                const quantities = productQuantities[id];
                return !quantities || !quantities.initial;
            });

        if (invalidProducts.length > 0) {
            toast.error("Veuillez définir un stock initial pour tous les produits sélectionnés");
            return;
        }

        setIsResetting(true);
        try {
            // Préparer les données pour l'API
            const productsToUpdate = Object.entries(selectedProducts)
                .filter(([_, selected]) => selected)
                .map(([id, _]) => {
                    const product = products.find(p => p.id === id);
                    return {
                        id,
                        initialStock: productQuantities[id].initial,
                        weeklySales: product.weeklySold || 0
                    };
                });

            const response = await axios.post(
                `${import.meta.env.VITE_API_URL}/api/back-office/stocks/restaurants/${selectedRestaurant.id}/update`,
                { products: productsToUpdate }
            );
            
            if (response.status === 200) {
                toast.success("Stocks mis à jour avec succès");
                fetchStockData(selectedRestaurant.id);
                setShowResetModal(false);
                setSelectedProducts({});
                setProductQuantities({});
            }
        } catch (error) {
            console.error("Erreur lors de la mise à jour des stocks:", error);
            toast.error("Échec de la mise à jour des stocks");
        } finally {
            setIsResetting(false);
        }
    };

    const filteredProducts = products.filter(product => {
        // Ne garder que les produits actifs
        if (!product.is_active) return false;
        
        // Puis appliquer les filtres de recherche
        if (searchTerm) {
            const searchValue = searchTerm.toLowerCase();
            
            switch (searchType) {
                case 'name':
                    if (!product.name.toLowerCase().includes(searchValue)) return false;
                    break;
                case 'category':
                    if (!product.category_name.toLowerCase().includes(searchValue)) return false;
                    break;
                case 'stock':
                    const stockValue = parseInt(searchValue, 10);
                    if (!isNaN(stockValue) && !(product.currentStock <= stockValue)) return false;
                    break;
                default:
                    break;
            }
        }
        
        return true;
    })
    // Trier les produits par nombre d'unités vendues (du plus au moins)
    .sort((a, b) => {
        // S'assurer que les valeurs sont des nombres
        const aSold = parseInt(a.soldInOrders || 0, 10);
        const bSold = parseInt(b.soldInOrders || 0, 10);
        // Tri décroissant (du plus grand au plus petit)
        return bSold - aSold;
    });

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
    
    const onPageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const getStockClassname = (stock) => {
        if (stock === 0) return 'bg-red-100 text-red-800 font-bold';
        if (stock <= 10) return 'bg-orange-100 text-orange-800';
        return 'bg-green-100 text-green-800';
    };

    // Affichage de la période hebdomadaire
    const WeeklyPeriodInfo = () => {
        if (!weeklyPeriod.start || !weeklyPeriod.end) return null;
        
        return (
            <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <h3 className="text-md font-semibold text-blue-800 mb-2">
                    Période des ventes hebdomadaires
                </h3>
                <p className="text-sm text-blue-700">
                    Du {weeklyPeriod.start} au {weeklyPeriod.end}
                </p>
            </div>
        );
    };

    // Modal de réinitialisation des stocks
    const ResetStockModal = () => {
        const [categories, setCategories] = useState([]);
        const [selectedCategories, setSelectedCategories] = useState({});
        const [categoryQuantities, setCategoryQuantities] = useState({});
        const [error, setError] = useState(null);

        // Charger les catégories disponibles
        useEffect(() => {
            const fetchCategories = async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/settings/categories/list`);
                    if (response.data) {
                        // Calculer les ventes hebdomadaires par catégorie
                        const categoriesWithSales = response.data.map(category => {
                            const productsInCategory = products.filter(p => p.category_id === category.id);
                            const totalWeeklySales = productsInCategory.reduce((total, product) => total + (product.weeklySold || 0), 0);
                            const productCount = productsInCategory.length;
                            
                            return {
                                ...category,
                                weeklySales: totalWeeklySales,
                                productCount
                            };
                        });

                        setCategories(categoriesWithSales);
                        
                        // Initialiser les états
                        const initialSelectedState = {};
                        const initialQuantityState = {};
                        
                        categoriesWithSales.forEach(category => {
                            initialSelectedState[category.id] = false;
                            initialQuantityState[category.id] = 1000; // Valeur par défaut
                        });
                        
                        setSelectedCategories(initialSelectedState);
                        setCategoryQuantities(initialQuantityState);
                    }
                } catch (error) {
                    console.error("Erreur lors du chargement des catégories:", error);
                    toast.error("Impossible de charger les catégories");
                }
            };
            
            if (showResetModal) {
                fetchCategories();
            }
        }, [showResetModal, products]);

        const handleCategorySelect = (categoryId) => {
            setSelectedCategories(prev => ({
                ...prev,
                [categoryId]: !prev[categoryId]
            }));
        };

        const handleQuantityChange = (categoryId, type, value) => {
            // Garantir que la valeur est un nombre positif
            const quantity = Math.max(0, parseInt(value, 10) || 0);
            
            setCategoryQuantities(prev => ({
                ...prev,
                [categoryId]: quantity
            }));
        };

        const handleResetStocks = async () => {
            // Vérifier qu'au moins une catégorie est sélectionnée
            const selectedCount = Object.values(selectedCategories).filter(Boolean).length;
            if (selectedCount === 0) {
                setError("Veuillez sélectionner au moins une catégorie");
                return;
            }

            setIsResetting(true);
            try {
                // Préparer les données pour l'API
                const categoriesToUpdate = categories
                    .filter(category => selectedCategories[category.id])
                    .map(category => ({
                        id: category.id,
                        initialStock: categoryQuantities[category.id],
                        products: products
                            .filter(p => p.category_id === category.id)
                            .map(p => ({
                                id: p.id,
                                weeklySales: p.weeklySold || 0
                            }))
                    }));

                const response = await axios.post(
                    `${import.meta.env.VITE_API_URL}/api/back-office/stocks/restaurants/${selectedRestaurant.id}/update-by-category`,
                    { categories: categoriesToUpdate }
                );
                
                if (response.status === 200) {
                    toast.success("Stocks mis à jour avec succès");
                    fetchStockData(selectedRestaurant.id);
                    setShowResetModal(false);
                    setSelectedCategories({});
                    setCategoryQuantities({});
                }
            } catch (error) {
                console.error("Erreur lors de la mise à jour des stocks:", error);
                toast.error("Échec de la mise à jour des stocks");
            } finally {
                setIsResetting(false);
            }
        };

        if (!showResetModal) return null;
        
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white p-6 rounded-lg w-full max-w-2xl shadow-xl">
                    <div className="mb-4">
                        <h3 className="text-lg font-bold text-primary">Mise à jour des stocks par catégorie</h3>
                        <p className="text-sm text-gray-600 mt-2">
                            Définissez le stock initial pour chaque catégorie. Le système déduira automatiquement les ventes hebdomadaires de chaque produit.
                        </p>
                    </div>

                    {error && (
                        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                            {error}
                        </div>
                    )}
                    
                    <div className="max-h-96 overflow-y-auto mb-4">
                        <table className="min-w-full border-collapse">
                            <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                    <th className="p-3 text-left text-sm font-medium text-gray-500">Sélection</th>
                                    <th className="p-3 text-left text-sm font-medium text-gray-500">Catégorie</th>
                                    <th className="p-3 text-left text-sm font-medium text-gray-500">Nombre de produits</th>
                                    <th className="p-3 text-left text-sm font-medium text-gray-500">Stock initial</th>
                                    <th className="p-3 text-left text-sm font-medium text-gray-500">Ventes hebdo. totales</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {categories.map(category => (
                                    <tr key={category.id} className="hover:bg-gray-50">
                                        <td className="p-3">
                                            <input
                                                type="checkbox"
                                                checked={selectedCategories[category.id] || false}
                                                onChange={() => handleCategorySelect(category.id)}
                                                className="h-4 w-4 text-primary focus:ring-primary rounded"
                                            />
                                        </td>
                                        <td className="p-3 text-sm text-gray-900">{category.name}</td>
                                        <td className="p-3 text-sm text-gray-900">{category.productCount}</td>
                                        <td className="p-3">
                                            <input
                                                type="number"
                                                min="0"
                                                value={categoryQuantities[category.id] || 0}
                                                onChange={(e) => handleQuantityChange(category.id, 'initial', e.target.value)}
                                                disabled={!selectedCategories[category.id]}
                                                className={`w-24 px-2 py-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                                                    !selectedCategories[category.id] ? 'bg-gray-100 text-gray-500' : ''
                                                }`}
                                            />
                                        </td>
                                        <td className="p-3 text-sm text-gray-900">
                                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                category.weeklySales > 0 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                                            }`}>
                                                {category.weeklySales}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    
                    <div className="flex justify-end space-x-3">
                        <button
                            onClick={() => {
                                setShowResetModal(false);
                                setSelectedCategories({});
                                setCategoryQuantities({});
                                setError(null);
                            }}
                            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                            disabled={isResetting}
                        >
                            Annuler
                        </button>
                        <button
                            onClick={handleResetStocks}
                            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/80 flex items-center"
                            disabled={isResetting}
                        >
                            {isResetting ? (
                                <>
                                    <RefreshCw size={16} className="mr-2 animate-spin" />
                                    Mise à jour...
                                </>
                            ) : (
                                "Mettre à jour les stocks"
                            )}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Modal pour réinitialiser les stocks par catégorie
    const ResetStockByCategoryModal = () => {
        const [categories, setCategories] = useState([]);
        const [selectedCategories, setSelectedCategories] = useState({});
        const [categoryQuantities, setCategoryQuantities] = useState({});
        const [error, setError] = useState(null);

        // Charger les catégories disponibles
        useEffect(() => {
            const fetchCategories = async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/settings/categories/list`);
                    if (response.data) {
                        setCategories(response.data);
                        
                        // Initialiser tous les états avec des valeurs par défaut
                        const initialSelectedState = {};
                        const initialQuantityState = {};
                        
                        response.data.forEach(category => {
                            initialSelectedState[category.id] = false;
                            initialQuantityState[category.id] = 1000; // Valeur par défaut
                        });
                        
                        setSelectedCategories(initialSelectedState);
                        setCategoryQuantities(initialQuantityState);
                    }
                } catch (error) {
                    console.error("Erreur lors du chargement des catégories:", error);
                    toast.error("Impossible de charger les catégories");
                }
            };
            
            if (showResetCategoryModal) {
                fetchCategories();
            }
        }, [showResetCategoryModal]);

        // Gérer le changement de sélection d'une catégorie
        const handleCategorySelect = (categoryId) => {
            setSelectedCategories(prev => ({
                ...prev,
                [categoryId]: !prev[categoryId]
            }));
        };

        // Gérer le changement de quantité d'une catégorie
        const handleQuantityChange = (categoryId, type, value) => {
            // Garantir que la valeur est un nombre positif
            const quantity = Math.max(0, parseInt(value, 10) || 0);
            
            setCategoryQuantities(prev => ({
                ...prev,
                [categoryId]: quantity
            }));
        };

        // Valider le formulaire avant soumission
        const validateForm = () => {
            const hasSelectedCategory = Object.values(selectedCategories).some(selected => selected);
            
            if (!hasSelectedCategory) {
                setError("Veuillez sélectionner au moins une catégorie");
                return false;
            }

            // Vérifier que toutes les catégories sélectionnées ont une quantité valide
            let valid = true;
            Object.entries(selectedCategories).forEach(([categoryId, isSelected]) => {
                if (isSelected) {
                    const quantity = categoryQuantities[categoryId];
                    if (quantity === undefined || isNaN(quantity)) {
                        valid = false;
                        setError(`Veuillez définir une quantité valide pour toutes les catégories sélectionnées`);
                    }
                }
            });

            if (valid) {
                setError(null);
            }
            
            return valid;
        };

        // Soumettre la réinitialisation des stocks par catégorie
        const handleSubmit = async () => {
            if (!validateForm()) return;
            
            setIsResetting(true);
            
            try {
                // Préparer les données pour l'API
                const categoriesToReset = Object.entries(selectedCategories)
                    .filter(([_, isSelected]) => isSelected)
                    .map(([categoryId, _]) => ({
                        id: categoryId,
                        quantity: categoryQuantities[categoryId]
                    }));
                
                // Appel à l'API
                const response = await axios.post(
                    `${import.meta.env.VITE_API_URL}/api/back-office/stocks/restaurants/${selectedRestaurant.id}/reset-by-category`,
                    { categories: categoriesToReset }
                );
                
                toast.success("Stocks réinitialisés avec succès pour les catégories sélectionnées");
                console.log("Réponse API:", response.data);
                
                // Fermer la modal et rafraîchir les données
                setShowResetCategoryModal(false);
                fetchStockData(selectedRestaurant.id);
                
            } catch (error) {
                console.error("Erreur lors de la réinitialisation des stocks par catégorie:", error);
                toast.error("Échec de la réinitialisation des stocks");
            } finally {
                setIsResetting(false);
            }
        };

        return (
            <>
                {showResetCategoryModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                        <div className="bg-white p-6 rounded-lg w-full max-w-2xl shadow-xl">
                            <div className="mb-4">
                                <h3 className="text-lg font-bold text-primary">Réinitialiser les stocks par catégorie</h3>
                                <p className="text-sm text-gray-600 mt-2">
                                    Sélectionnez les catégories dont vous souhaitez réinitialiser les stocks et définissez la quantité souhaitée pour chacune.
                                </p>
                            </div>
                            
                            {error && (
                                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                                    {error}
                                </div>
                            )}
                            
                            <div className="max-h-96 overflow-y-auto mb-4">
                                <table className="min-w-full border-collapse">
                                    <thead className="bg-gray-50 sticky top-0">
                                        <tr>
                                            <th className="p-3 text-left text-sm font-medium text-gray-500">Sélection</th>
                                            <th className="p-3 text-left text-sm font-medium text-gray-500">Catégorie</th>
                                            <th className="p-3 text-left text-sm font-medium text-gray-500">Quantité</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {categories.map(category => (
                                            <tr key={category.id} className="hover:bg-gray-50">
                                                <td className="p-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedCategories[category.id] || false}
                                                        onChange={() => handleCategorySelect(category.id)}
                                                        className="h-4 w-4 text-primary focus:ring-primary rounded"
                                                    />
                                                </td>
                                                <td className="p-3 text-sm text-gray-900">{category.name}</td>
                                                <td className="p-3">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        value={categoryQuantities[category.id] || 0}
                                                        onChange={(e) => handleQuantityChange(category.id, 'initial', e.target.value)}
                                                        disabled={!selectedCategories[category.id]}
                                                        className={`w-24 px-2 py-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                                                            !selectedCategories[category.id] ? 'bg-gray-100 text-gray-500' : ''
                                                        }`}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => setShowResetCategoryModal(false)}
                                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                                    disabled={isResetting}
                                >
                                    Annuler
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/80 flex items-center"
                                    disabled={isResetting}
                                >
                                    {isResetting ? (
                                        <>
                                            <RefreshCw size={16} className="mr-2 animate-spin" />
                                            Réinitialisation...
                                        </>
                                    ) : (
                                        "Valider"
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    };

    // Composant pour afficher les statistiques sous forme de cartes
    const StockStatCards = () => {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-blue-500">
                    <div className="flex justify-between items-center">
                        <div>
                            <p className="text-sm text-gray-500 mb-1">Stock total</p>
                            <p className="text-2xl font-bold">{formatNumber(stockSummary.totalStock)}</p>
                        </div>
                        <div className="bg-blue-100 p-3 rounded-full">
                            <Package size={20} className="text-blue-500" />
                        </div>
                    </div>
                </div>
                
                <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-red-500">
                    <div className="flex justify-between items-center">
                        <div>
                            <p className="text-sm text-gray-500 mb-1">Produits en rupture</p>
                            <p className="text-2xl font-bold">{formatNumber(stockSummary.outOfStock)}</p>
                        </div>
                        <div className="bg-red-100 p-3 rounded-full">
                            <AlertTriangle size={20} className="text-red-500" />
                        </div>
                    </div>
                </div>
                
                <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-orange-500">
                    <div className="flex justify-between items-center">
                        <div>
                            <p className="text-sm text-gray-500 mb-1">Stock faible (≤ 10)</p>
                            <p className="text-2xl font-bold">{formatNumber(stockSummary.lowStock)}</p>
                        </div>
                        <div className="bg-orange-100 p-3 rounded-full">
                            <TrendingDown size={20} className="text-orange-500" />
                        </div>
                    </div>
                </div>
                
                <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-green-500">
                    <div className="flex justify-between items-center">
                        <div>
                            <p className="text-sm text-gray-500 mb-1">Meilleure vente</p>
                            <p className="text-lg font-bold truncate max-w-32" title={stockSummary.bestSeller.name}>
                                {stockSummary.bestSeller.name || 'Aucun'}
                            </p>
                            <p className="text-sm text-gray-500">{formatNumber(stockSummary.bestSeller.sold)} vendus</p>
                        </div>
                        <div className="bg-green-100 p-3 rounded-full">
                            <TrendingUp size={20} className="text-green-500" />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Calculer le total des unités vendues pour la date filtrée
    const calculateTotalSoldForDate = () => {
        if (!products || products.length === 0) return 0;
        return products.reduce((total, product) => total + (product.soldInOrders || 0), 0);
    };

    return (
        <BOLayout>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">
                    Gestion des Stocks
                </h1>
                
                <div className="flex items-center gap-2">
                    <select
                        className="px-4 py-2 border border-gray-300 rounded-md bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        value={selectedRestaurant?.id || ''}
                        onChange={handleRestaurantChange}
                    >
                        {restaurants.map(restaurant => (
                            <option key={restaurant.id} value={restaurant.id}>
                                {restaurant.name}
                            </option>
                        ))}
                    </select>
                    
                    <button
                        onClick={() => fetchStockData(selectedRestaurant?.id)}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors shadow-sm"
                        disabled={loading}
                    >
                        {loading ? (
                            <RefreshCw size={16} className="animate-spin" />
                        ) : (
                            <RefreshCw size={16} />
                        )}
                        <span>Actualiser</span>
                    </button>
                    
                    <button
                        onClick={() => setShowResetModal(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors shadow-sm"
                    >
                        <RefreshCw size={16} />
                        <span>Mettre à jour les stocks</span>
                    </button>

                    <button
                        onClick={() => setShowResetCategoryModal(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors shadow-sm"
                    >
                        <Filter size={16} />
                        <span>Réinitialiser par catégorie</span>
                    </button>
                </div>
            </div>
            
            {alertCount > 0 && (
                <div className="flex items-center gap-2 bg-red-100 text-red-800 px-4 py-2 rounded-md mb-6">
                    <AlertTriangle size={18} />
                    <span className="font-semibold">{alertCount} produit{alertCount > 1 ? 's' : ''} en alerte de stock</span>
                </div>
            )}

            {loading ? (
                <div className="flex flex-col justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                    <p className="mt-4 text-gray-500">Chargement des données de stock...</p>
                </div>
            ) : (
                <>
                    <StockStatCards />
                    
                    <div className="bg-white p-4 rounded-lg shadow-md mb-6">
                        <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center mb-4">
                            <h2 className="text-lg font-semibold flex items-center gap-2">
                                <Filter size={18} />
                                Filtrer les produits
                            </h2>
                            
                            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                                <div className="relative w-full sm:w-64">
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        placeholder="Rechercher..."
                                        className="w-full px-4 py-2 border border-gray-300 rounded-md pl-10 focus:outline-none focus:ring-2 focus:ring-primary"
                                    />
                                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                                        <Search size={18} />
                                    </span>
                                </div>
                                
                                <select
                                    value={searchType}
                                    onChange={(e) => setSearchType(e.target.value)}
                                    className="px-4 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                                >
                                    <option value="name">Nom du produit</option>
                                    <option value="category">Catégorie</option>
                                    <option value="stock">Stock ≤</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {alerts.length > 0 && (
                        <div className="mb-8">
                            <h2 className="text-lg font-bold mb-4 flex items-center">
                                <AlertTriangle size={20} className="mr-2 text-red-600" />
                                Produits en Alerte de Stock
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {alerts.map((product) => (
                                    <div 
                                        key={product.id} 
                                        className={`bg-white rounded-lg shadow-md overflow-hidden border-l-4 ${
                                            product.alert_level === 'critique' ? 'border-red-500' : 'border-orange-500'
                                        }`}
                                    >
                                        <div className="p-4">
                                            <div className="flex items-center gap-3 mb-2">
                                                {product.image_url ? (
                                                    <img 
                                                        src={product.image_url} 
                                                        alt={product.name} 
                                                        className="h-12 w-12 rounded-full object-cover border border-gray-200"
                                                    />
                                                ) : (
                                                    <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center">
                                                        <Package size={20} className="text-gray-400" />
                                                    </div>
                                                )}
                                                <div>
                                                    <h3 className="font-semibold text-gray-800">{product.name}</h3>
                                                    <p className="text-sm text-gray-500">{product.category_name}</p>
                                                </div>
                                            </div>
                                            <div className="flex justify-between items-center mt-3">
                                                <div>
                                                    <span className="text-sm text-gray-500">Stock actuel:</span>
                                                    <span className={`ml-2 px-2 py-1 rounded-full text-xs font-semibold ${
                                                        product.alert_level === 'critique' 
                                                            ? 'bg-red-100 text-red-800' 
                                                            : 'bg-orange-100 text-orange-800'
                                                    }`}>
                                                        {product.currentStock}
                                                    </span>
                                                </div>
                                                <div className="text-sm font-medium">
                                                    {product.price}€
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
                        <div className="overflow-x-auto">
                            <WeeklyPeriodInfo />
                            
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Nom
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Catégorie
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Stock Disponible
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Total Vendu
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Vendu cette semaine
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {currentItems.map((product) => (
                                        <tr key={product.id}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {product.name}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-500">
                                                    {product.category_name || "Sans catégorie"}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStockClassname(product.currentStock)}`}>
                                                    {product.currentStock}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {product.soldInOrders || 0}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${product.weeklySold > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                                    {product.weeklySold || 0}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <button 
                                                    onClick={() => navigate(`/back-office/products/${product.id}/edit`)}
                                                    className="text-primary hover:text-primary-dark"
                                                >
                                                    <Edit size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="flex justify-center">
                        <Pagination
                            currentPage={currentPage}
                            totalItems={filteredProducts.length}
                            itemsPerPage={itemsPerPage}
                            onPageChange={onPageChange}
                        />
                    </div>
                </>
            )}

            <ResetStockModal />
            <ResetStockByCategoryModal />
        </BOLayout>
    );
};

export default BOStocks; 